import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const TOOLTIP_GAP = 8;
const VIEWPORT_MARGIN = 8;

const NotificationInfoIcon = ({ label, lines }) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const visibleLines = (lines || []).filter(Boolean);

    useLayoutEffect(() => {
        if (!open || !triggerRef.current || !tooltipRef.current) {
            return;
        }

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const belowTop = triggerRect.bottom + TOOLTIP_GAP;
        const aboveTop = triggerRect.top - tooltipRect.height - TOOLTIP_GAP;
        const fitsBelow = belowTop + tooltipRect.height <= window.innerHeight - VIEWPORT_MARGIN;
        const preferredTop = fitsBelow ? belowTop : aboveTop;
        const maxTop = Math.max(
            VIEWPORT_MARGIN,
            window.innerHeight - tooltipRect.height - VIEWPORT_MARGIN
        );
        const top = Math.min(Math.max(VIEWPORT_MARGIN, preferredTop), maxTop);
        const preferredLeft = triggerRect.right + TOOLTIP_GAP;
        const maxLeft = Math.max(
            VIEWPORT_MARGIN,
            window.innerWidth - tooltipRect.width - VIEWPORT_MARGIN
        );
        const left = Math.min(Math.max(VIEWPORT_MARGIN, preferredLeft), maxLeft);

        setPosition({ top, left });
    }, [open, visibleLines.length]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const closeTooltip = () => setOpen(false);
        window.addEventListener("scroll", closeTooltip, true);
        window.addEventListener("resize", closeTooltip);

        return () => {
            window.removeEventListener("scroll", closeTooltip, true);
            window.removeEventListener("resize", closeTooltip);
        };
    }, [open]);

    const openTooltip = () => {
        setPosition(null);
        setOpen(true);
    };

    const toggleTooltip = () => {
        setOpen((prev) => {
            if (!prev) {
                setPosition(null);
            }
            return !prev;
        });
    };

    const tooltip = open && visibleLines.length > 0 && (
        <span
            ref={tooltipRef}
            role="tooltip"
            className="fixed z-[120] w-[320px] rounded-md border border-gray-200 bg-white p-3 text-left text-xs font-normal leading-relaxed text-gray-600 shadow-lg"
            style={position ? { top: position.top, left: position.left } : { visibility: "hidden" }}
        >
            {visibleLines.map((line) => (
                <span key={line} className="mt-1 block first:mt-0">{line}</span>
            ))}
        </span>
    );

    return (
        <span className="relative inline-flex">
            <button
                type="button"
                ref={triggerRef}
                aria-label={`What ${label} is for`}
                aria-expanded={open}
                onMouseEnter={openTooltip}
                onMouseLeave={() => setOpen(false)}
                onFocus={openTooltip}
                onBlur={() => setOpen(false)}
                onClick={toggleTooltip}
                className="rounded-full text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1"
            >
                <InformationCircleIcon className="h-4 w-4" />
            </button>
            {tooltip && createPortal(tooltip, document.body)}
        </span>
    );
};

export default NotificationInfoIcon;
